import {
  AgecProductPagePlan,
  BusinessType,
  CertificatesPlan,
  CustomFieldsPlan,
  LangageIso6391,
  LCACustomizationPlan,
  OutputCustomizationPlan,
  RduePlan,
  ZalandoPlan,
} from "@tfg/db"
import { kebabCase } from "lodash"
import { z } from "zod"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { paginationOptionsSchema } from "@tfg/types/schemas/common"
import {
  CertificateAttributeSchema,
  CertificateCategorySchema,
  CertificateTriggerSchema,
  MaterialTypeCategorySchema,
  UndeliveredEventsSchema,
} from "@tfg/types/schemas/enum"

import { standardEmail, standardString, stringToJSONSchema } from "./utils"

export const productTypeUpdateSchema = z.object({
  name: z.string().min(1).transform(kebabCase),
  operationChainId: z.string().uuid(),
  ecobalyseId: z.string().transform(kebabCase).optional(),
})
export type ProductTypeUpdateValidationType = z.infer<
  typeof productTypeUpdateSchema
>

export const materialTypeSchema = z.object({
  key: z.string().min(1).transform(kebabCase),
  category: MaterialTypeCategorySchema,
  ecobalyseId: z.string().nullable(),
  operationChainId: z.string().uuid(),
})
export type materialTypeUpdateValidationType = z.infer<
  typeof materialTypeSchema
>

export const organizationSchema = z.object({
  name: z.string(),
  agecProductPagePlan: z.nativeEnum(AgecProductPagePlan),
  outputCustomizationPlan: z.nativeEnum(OutputCustomizationPlan),
  lcaCustomizationPlan: z.nativeEnum(LCACustomizationPlan),
  customFieldsPlan: z.nativeEnum(CustomFieldsPlan),
  certificatesPlan: z.nativeEnum(CertificatesPlan),
  zalandoPlan: z.nativeEnum(ZalandoPlan),
  rduePlan: z.nativeEnum(RduePlan),
  businessType: z.nativeEnum(BusinessType),
})
export type organizationValidationType = z.infer<typeof organizationSchema>

export const updateOrganizationSchema = organizationSchema.partial()
export type updateOrganizationValidationType = z.infer<
  typeof updateOrganizationSchema
>

export const inviteUsersBodySchema = z.object({
  users: z.array(
    z.object({
      name: standardString(),
      email: standardEmail(),
    })
  ),
})
export type inviteUsersBodyValidationType = z.infer<
  typeof inviteUsersBodySchema
>

export const translationUpdateSchema = z.object({
  id: z.number(),
  translatedLanguage: z.nativeEnum(LangageIso6391),
  translation: z.string(),
}) satisfies z.ZodType<LibraryResponses.TranslationUpdate>
export type translationUpdateValidationType = z.infer<
  typeof translationUpdateSchema
>

export const refreshTokenRequestSchemaCount = z.object({
  filterOption: z.enum(["all", "client", "supplier", "admin"]),
  searchValue: z.string().optional(),
})
export type refreshTokenRequestSchemaCountValidationType = z.infer<
  typeof refreshTokenRequestSchemaCount
>

export const fetchDataOptionsSchema = z.object({
  pageIndex: z
    .string()
    .refine((str) => /^\d+$/.test(str), {
      message: "pageIndex must be a stringified number",
    })
    .transform(Number),
  pageSize: z
    .string()
    .refine((str) => /^\d+$/.test(str), {
      message: "pageSize must be a stringified number",
    })
    .transform(Number),
})
export type fetchDataOptionsValidationType = z.infer<
  typeof fetchDataOptionsSchema
>

export const prismaSortingSchema = z.array(
  z.object({
    updatedAt: z.enum(["asc", "desc"]).optional(),
    createdAt: z.enum(["asc", "desc"]).optional(),
  })
)
export type prismaSortingValidationType = z.infer<typeof prismaSortingSchema>

export const refreshTokenRequestSchema = z.object({
  fetchDataOptions: fetchDataOptionsSchema,
  prismaSorting: prismaSortingSchema.optional(),
  filterOption: z.enum(["all", "client", "supplier", "admin"]),
  searchValue: z.string().optional(),
})
export type refreshTokenRequestValidationType = z.infer<
  typeof refreshTokenRequestSchema
>

export const magicLinkRequestSchema = z.object({
  fetchDataOptions: fetchDataOptionsSchema,
  prismaSorting: prismaSortingSchema.optional(),
})
export type magicLinkRequestValidationType = z.infer<
  typeof magicLinkRequestSchema
>

export const createDefaultCountryContactSchema = z.object({
  forEmails: z.array(z.string()),
})
export type createDefaultCountryContactValidationType = z.infer<
  typeof createDefaultCountryContactSchema
>

export const materialTypeRequestSchema = z.object({
  materialOpChainName: z.string().optional(),
  showAll: z.coerce.boolean().optional(),
})
export type materialTypeRequestValidationType = z.infer<
  typeof materialTypeRequestSchema
>

export const InvoiceUncheckedCreateInputSchema = z
  .object({
    id: z.number().int().optional(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
    reference: z.string(),
    issueDate: z.coerce.date().optional(),
    amount: z.number(),
    organizationId: z.number().int(),
    documentId: z.string().uuid(),
  })
  .strict()

export const standardsCreateSchema = z.object({
  name: standardString().min(1),
  certificateTypes: z.array(z.string().uuid()).min(1),
})
export type standardsCreateValidationType = z.infer<
  typeof standardsCreateSchema
>

export const standardsUpdateSchema = z.object({
  id: z.number(),
  name: standardString().optional(),
  certificateTypes: z.array(z.string().uuid()).optional(),
})
export type standardsUpdateValidationType = z.infer<
  typeof standardsUpdateSchema
>
export const certificatefiltersSchema = z.object({
  trigger: CertificateTriggerSchema.optional(),
  category: CertificateCategorySchema.optional(),
})

export type certifcatefiltersValidationType = z.infer<
  typeof certificatefiltersSchema
>
export const getCertificateTypesSchema = z.object({
  fetchDataOptions: fetchDataOptionsSchema,
  searchCertificateType: z.string().optional(),
  filters: stringToJSONSchema.pipe(certificatefiltersSchema).optional(),
})

export type getCertifcateValidationType = z.infer<
  typeof getCertificateTypesSchema
>

export const certificateTypeCreateSchema = z.object({
  name: standardString().min(1),
  standard: z
    .array(
      z.string().refine((str) => /^\d+$/.test(str), {
        message: "Each standard must be a stringified number",
      })
    )
    .transform((arr) => arr.map(Number))
    .optional(),
  category: CertificateCategorySchema,
  trigger: CertificateTriggerSchema,
  attributes: CertificateAttributeSchema.optional(),
})

export type certificateTypeCreateValidationType = z.infer<
  typeof certificateTypeCreateSchema
>

export const certificateTypeUpdateSchema = z.object({
  id: z.string().uuid(),
  name: standardString().min(1).optional(),
  standard: z.array(z.number()).optional(),
  category: CertificateCategorySchema.optional(),
  trigger: CertificateTriggerSchema.optional(),
  attributes: CertificateAttributeSchema.array().optional(),
})
export type certificateTypeUpdateValidationType = z.infer<
  typeof certificateTypeUpdateSchema
>

export const reopenRequestSchema = z.object({
  mode: z.enum(["clean", "dirty"]),
})

export const reopenRequestParamsSchema = z.object({
  id: z.coerce.number(),
})

export const BouncedEmailsfiltersSchema = z.object({
  event: UndeliveredEventsSchema.optional().nullable(),
  inviterCompanyId: z.string().uuid().optional().nullable(),
  supplierEmail: z.string().optional().nullable(),
  date: z
    .string()
    .transform((str) => new Date(str))
    .optional()
    .nullable(),
})
export type findBouncedEmailsFilterType = z.infer<
  typeof BouncedEmailsfiltersSchema
>

export const findBouncedEmailsRequestSchema = z.object({
  fetchDataOptions: paginationOptionsSchema.optional(),
  filters: BouncedEmailsfiltersSchema.optional(),
})

export const translationFiltersSchema = z.object({
  search: z.string().default(""),
})
